'use client'

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { logoutUser, getUserInfo } from '@/api/auth'
import { getSidebarData } from '@/api/console'
import { SidebarData } from '@/common/interface/console'
import { SessionUser } from '@/common/interface/auth'
import { clearSessionCookie } from '@/components/utils' // hasSessionCookie

interface AuthContextType {
  user: SessionUser | null
  isAuthenticated: boolean
  isLoading: boolean
  sidebarData: SidebarData | null
  login: (user: SessionUser) => void
  logout: () => void
  loadSidebarData: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [sidebarData, setSidebarData] = useState<SidebarData | null>(null)

  const logout = useCallback(async () => {
    try {
      // 서버에 로그아웃 요청
      await logoutUser()
    } catch (error) {
      console.error('로그아웃 요청 실패:', error)
    } finally {
      // 클라이언트 상태 정리
      setUser(null)
      setIsAuthenticated(false)
      setSidebarData(null)
      // 세션 쿠키 삭제
      clearSessionCookie()
    }
  }, [])

  const loadSidebarData = async () => {
    try {
      // 인증 상태와 관계없이 사이드바 데이터를 로드 시도
      const data = await getSidebarData()
      setSidebarData(data)
    } catch (error) {
      console.error('사이드바 데이터 로드 실패:', error)
      setSidebarData(null)
    }
  }

  useEffect(() => {
    let isMounted = true

    const initializeAuth = async () => {
      try {
        // 세션 쿠키가 있는지 확인
        // if (hasSessionCookie()) {
        const userData = await getUserInfo()

        if (isMounted && userData && userData.isAuthenticated) {
          // console.info('인증 성공:', userData);

          // 세션이 유효하면 사용자 정보를 서버에서 가져옴
          setUser({
            schoolCode: userData.user.schoolCode,
            schoolName: userData.user.schoolName,
            teacherCode: userData.user.teacherCode,
            name: userData.user.name,
            role: userData.user.role,
            auth: userData.user.auth,
            options: userData.user.options,
          })
          setIsAuthenticated(true)
          // 사이드바 데이터 로드 (인증 상태 설정 후)
          await loadSidebarData()
        } else if (isMounted) {
          // console.info('세션 유효하지 않음, 로그아웃 처리');
          // 세션이 유효하지 않으면 로그아웃
          logout()
        }
        // } else if (isMounted) {
        //   console.info('세션 쿠키 없음');
        // }
      } catch (error) {
        console.error('인증 초기화 실패:', error)
        if (isMounted) {
          logout()
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    initializeAuth()

    return () => {
      isMounted = false
    }
  }, [logout])

  const login = async (userData: SessionUser) => {
    setUser(userData)
    setIsAuthenticated(true)
    // 로그인 후 사이드바 데이터 로드
    await loadSidebarData()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        sidebarData,
        login,
        logout,
        loadSidebarData,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
